import { Collapsible, CollapsibleContent, Input } from '@/components/ui';
import { FormField } from '@/components/ui/form';
import { ScrollArea } from '@/components/ui/scroll-area';
import TooltipWrapper from '@/components/ui/tooltip';
import {
  CreateOrderInput,
  createOrderInputSchema,
  CreateOrderResponse,
  useCreateOrder,
  useCreateScaleOrder,
} from '@/features/trade/order/api/create-order';
import { OrderCheckbox } from '@/features/trade/order/components';
import {
  FormItem,
  FormLabel,
} from '@/features/trade/order/components/order-form-components';
import {
  getIsMigrationTradingBlocked,
  useMigrationTradingBlock,
} from '@/hooks/use-migration';
import useSymbol from '@/hooks/use-symbol';
import { getIsWeekendTradingBlocked } from '@/hooks/use-weekend-trading';
import { cn } from '@/lib/utils';
import { getCurrentAccount } from '@/store/use-account-store';
import { useMarketStore } from '@/store/use-markets-store';
import { useOrdersStore } from '@/store/use-orders-store';
import { useSettingsStore } from '@/store/use-settings-store';
import {
  EventType,
  MarketStatus,
  OrderStatus,
  OrderType,
  TimeInForce,
} from '@/types/enums';
import { sendEvent } from '@/utils/analytics';
import {
  bigIntToDecimalStr,
  parseDecimalToBigInt,
  parseInputToDecimalStr,
  validatedDecimalStr,
} from '@/utils/value-format';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { useStore } from 'zustand';

import {
  validateFinalLimitPrice,
  validateOptionalFinalLimitPrice,
} from '../utils/helpers';
import {
  generateScaleOrders,
  getLimitPriceFromMaxSlippage,
  getSlippageReferencePrice,
} from '../utils/math';
import ClosePositionConfirm from './close-position-confirm';
import AvailableFunds from './form/available-funds';
import EstFillPrice from './form/est-fill-price';
import EstLiqPrice from './form/est-liq-price';
import Fee from './form/fee';
import OrderTypeTabs from './form/order-type';
import OrderValue from './form/order-value';
import ReduceOnly from './form/reduce-only';
import SideTabs from './form/side';
import Slippage from './form/slippage';
import SubmitButton from './form/submit-button';
import TpslInput from './form/tpsl-input';
import LimitsBreachConfirm, {
  getLimitsBreachInfo,
  LimitsBreachInfo,
} from './limits-breach-confirm';
import { getDefaultMaxSlippageValue } from './max-slippage-input';
import OrderConfirm, { getShouldConfirm } from './order-confirm';
import { PriceInput } from './price-input';
import ScaleOrderConfirm from './scale-order-confirm';
import SizeInput from './size-input';
import TIFSelect from './tif-select';

const formValuesSchema = z.object({
  order: createOrderInputSchema,
  tp: z.object({
    // size: z.string().optional(),
    limitPrice: z.string(),
  }),
  sl: z.object({
    // size: z.string().optional(),
    limitPrice: z.string(),
  }),
  startPrice: z
    .string()
    .optional()
    .or(
      z
        .string()
        .regex(/^[0-9]\d*(\.\d+)?$/, 'Must be a positive number')
        .regex(/^(?!0*\.?0+$)/, 'Must not be zero or all zeros'),
    ),
  endPrice: z
    .string()
    .optional()
    .or(
      z
        .string()
        .regex(/^[0-9]\d*(\.\d+)?$/, 'Must be a positive number')
        .regex(/^(?!0*\.?0+$)/, 'Must not be zero or all zeros'),
    ),
  ordersCount: z
    .string()
    .optional()
    .or(
      z
        .string()
        .regex(/^[0-9]\d*$/, 'Must be a positive integer')
        .refine(
          (v) => Number.parseInt(v, 10) >= 2 && Number.parseInt(v, 10) <= 10,
          'Must be greater than or equal to 2 and less than or equal to 10',
        ),
    ),
  sizeSkew: z
    .string()
    .optional()
    .or(
      z
        .string()
        .regex(/^[0-9]\d*(\.\d+)?$/, 'Must be a positive number')
        .regex(/^(?!0*\.?0+$)/, 'Must not be zero or all zeros'),
    ),
});

export type OrderFormSchema = z.infer<typeof formValuesSchema>;

const DEFAULT_SIZE_PCT: number = 0;

export default function OrderForm() {
  const symbol = useSymbol();
  const { t } = useTranslation();

  const isReduceOnly = useStore(
    useMarketStore(symbol),
    (state) => state.marketSpec.status === MarketStatus.REDUCE_ONLY,
  );
  const { isBlocked: isMigrationBlocked } = useMigrationTradingBlock();

  const defaultValues = useMemo<OrderFormSchema>(() => {
    return {
      order: {
        symbol,
        isBuy: true,
        size: '',
        orderType: OrderType.MARKET,
        limitPrice: '',
        reduceOnly: isReduceOnly,
        timeInForce: TimeInForce.GTC,
      },
      tp: {
        // size: '',
        limitPrice: '',
      },
      sl: {
        // size: '',
        limitPrice: '',
      },
      startPrice: '',
      endPrice: '',
      ordersCount: '',
      sizeSkew: '1.0',
    };
  }, [symbol, isReduceOnly]);

  const form = useForm<OrderFormSchema>({
    resolver: zodResolver(formValuesSchema),
    defaultValues,
  });

  const isConfirmSubmitted = useRef<boolean>(false);
  const isScaleOrderConfirmSubmitted = useRef<boolean>(false);
  const isClosePositionConfirmSubmitted = useRef<boolean>(false);
  const isLimitsBreachConfirmSubmitted = useRef<boolean>(false);
  const preConfirmLimitPrice = useRef<string | null>(null);
  const wasConfirmSubmitted = useRef<boolean>(false);
  const [isSubmitSuccessful, setIsSubmitSuccessful] = useState(false);

  const {
    formState: { isSubmitting },
  } = form;

  const [tpslChecked, setTpslChecked] = useState(false);

  const isBuy = form.watch('order.isBuy');
  const orderType = form.watch('order.orderType');
  const sizeStr = form.watch('order.size');
  const startPrice = form.watch('startPrice');
  const endPrice = form.watch('endPrice');
  const ordersCount = form.watch('ordersCount');
  const sizeSkew = form.watch('sizeSkew');

  const tpPrice = form.watch('tp.limitPrice');
  const slPrice = form.watch('sl.limitPrice');

  const resetForm = () => {
    const { marketData, marketSpec } = useMarketStore(symbol).getState();

    const limitPrice = bigIntToDecimalStr(
      marketData.midPrice,
      marketSpec.priceDecimals,
    );

    form.reset({
      ...defaultValues,
      ...(orderType === OrderType.SCALE
        ? {
            startPrice: limitPrice,
            endPrice: limitPrice,
          }
        : {}),
      order: {
        ...defaultValues.order,
        symbol,
        isBuy,
        orderType,
        ...(orderType === OrderType.MARKET
          ? {
              limitPrice: getLimitPriceFromMaxSlippage(
                isBuy,
                parseDecimalToBigInt(
                  getDefaultMaxSlippageValue(symbol).toString(),
                  2n,
                ),
                getSlippageReferencePrice(symbol, isBuy),
                marketSpec.priceDecimals,
              ).decimal,
            }
          : {
              limitPrice,
            }),
      },
    });

    setIsSubmitSuccessful(false);
    setTimeout(() => setIsSubmitSuccessful(true));
    setTpslChecked(false);
  };

  useEffect(() => {
    resetForm();
  }, [symbol]);

  const scaleOrders = useMemo(() => {
    return generateScaleOrders(form.getValues(), sizeStr);
  }, [sizeStr, startPrice, endPrice, ordersCount, sizeSkew]);

  useEffect(() => {
    const limitPriceNum = Number(form.getValues('order.limitPrice'));

    useOrdersStore.setState({
      creationFormOrderType: orderType,
      creationFormEstimateFillPrice:
        orderType === OrderType.LIMIT && !Number.isNaN(limitPriceNum)
          ? limitPriceNum
          : undefined,
    });
  }, [orderType]);

  useEffect(() => {
    if (tpslChecked) {
      const tpPriceNum = Number(tpPrice);
      const slPriceNum = Number(slPrice);

      useOrdersStore.setState({
        creationFormTPPrice: Number.isNaN(tpPriceNum) ? undefined : tpPriceNum,
        creationFormSLPrice: Number.isNaN(slPriceNum) ? undefined : slPriceNum,
      });
    } else {
      useOrdersStore.setState({
        creationFormTPPrice: undefined,
        creationFormSLPrice: undefined,
      });
    }
  }, [tpslChecked, tpPrice, slPrice]);

  const [isConfirmOpen, setIsConfirmOpen] = useState<boolean>(false);
  const [isScaleOrderConfirmOpen, setIsScaleOrderConfirmOpen] =
    useState<boolean>(false);
  const [isClosePositionConfirmOpen, setIsClosePositionConfirmOpen] =
    useState<boolean>(false);
  const [isLimitsBreachConfirmOpen, setIsLimitsBreachConfirmOpen] =
    useState<boolean>(false);
  const [limitsBreachInfo, setLimitsBreachInfo] =
    useState<LimitsBreachInfo | null>(null);

  const formRef = useRef<HTMLFormElement | null>(null);

  const { mutateAsync: createOrder } = useCreateOrder();
  const { mutateAsync: createScaleOrder } = useCreateScaleOrder();

  const restoreLimitPriceOnCancel = () => {
    if (preConfirmLimitPrice.current !== null) {
      form.setValue('order.limitPrice', preConfirmLimitPrice.current);
      preConfirmLimitPrice.current = null;
    }
  };

  const resetConfirmFlags = () => {
    isConfirmSubmitted.current = false;
    isScaleOrderConfirmSubmitted.current = false;
    isClosePositionConfirmSubmitted.current = false;
    isLimitsBreachConfirmSubmitted.current = false;
  };

  // An open confirmation can no longer be honoured once the migration block
  // starts (`onSubmit` refuses it), so close it the way the close-position and
  // TP/SL dialogs do instead of leaving a dead button behind.
  useEffect(() => {
    if (!isMigrationBlocked) return;
    setIsConfirmOpen(false);
    setIsScaleOrderConfirmOpen(false);
    setIsClosePositionConfirmOpen(false);
    setIsLimitsBreachConfirmOpen(false);
    resetConfirmFlags();
    wasConfirmSubmitted.current = false;
    restoreLimitPriceOnCancel();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMigrationBlocked]);

  async function onSubmit(values: OrderFormSchema) {
    setIsSubmitSuccessful(false);

    // Belt and braces: the submit button is already disabled while the weekend
    // pause or the migration block is on, but a queued confirm dialog can
    // resolve after either starts.
    if (getIsWeekendTradingBlocked(symbol) || getIsMigrationTradingBlocked()) {
      resetConfirmFlags();
      return;
    }

    const { marketSpec } = useMarketStore(symbol).getState();

    const {
      showScaleOrderConfirm,
      showClosePositionConfirm,
      preserveMarketOrderFormOnCreate,
    } = useSettingsStore.getState();

    const currentPosition = getCurrentAccount(
      (state) => state.positions[symbol],
    );

    if (!isConfirmSubmitted.current && getShouldConfirm(values)) {
      preConfirmLimitPrice.current = values.order.limitPrice;

      sendEvent(EventType.SLIPPAGE_EXCEEDED, {
        symbol: values.order.symbol,
        size: values.order.size,
        stage: 'open',
      });

      setIsConfirmOpen(true);
    } else if (
      values.order.orderType === OrderType.MARKET &&
      currentPosition &&
      currentPosition.isLong !== values.order.isBuy &&
      showClosePositionConfirm &&
      !isClosePositionConfirmSubmitted.current
    ) {
      setIsClosePositionConfirmOpen(true);
    } else if (
      values.order.orderType === OrderType.SCALE &&
      showScaleOrderConfirm &&
      !isScaleOrderConfirmSubmitted.current
    ) {
      setIsScaleOrderConfirmOpen(true);
    } else if (values.order.orderType === OrderType.SCALE) {
      resetConfirmFlags();

      const order = values.order;

      const validatedOrders = scaleOrders.map((scaleOrder) => ({
        ...order,
        orderType: OrderType.LIMIT,
        size: parseInputToDecimalStr(
          scaleOrder.order.size,
          marketSpec.sizeDecimals,
        ),
        limitPrice: parseInputToDecimalStr(
          scaleOrder.order.limitPrice,
          marketSpec.priceDecimals,
        ),
      }));

      createScaleOrder({
        orders: validatedOrders,
        showToast: true,
        waitForConfirm: true,
      })
        .then((result) => {
          const results = Array.isArray(result) ? result : [result];

          if (!results.some((item) => item.status === OrderStatus.REJECTED)) {
            resetForm();
          }
        })
        .catch((code) => {
          console.warn('Scale order rejected:', code);
        });
    } else {
      const breachInfo = getLimitsBreachInfo(values);

      if (breachInfo && !isLimitsBreachConfirmSubmitted.current) {
        setLimitsBreachInfo(breachInfo);
        setIsLimitsBreachConfirmOpen(true);
        return;
      }

      resetConfirmFlags();

      const queue: (() => Promise<CreateOrderResponse>)[] = [];
      const order = values.order;

      const size = parseInputToDecimalStr(
        values.order.size,
        marketSpec.sizeDecimals,
      );

      const limitPrice = validateFinalLimitPrice(
        symbol,
        values.order.limitPrice,
      );

      const validatedTpPrice = validateOptionalFinalLimitPrice(
        symbol,
        values.tp.limitPrice,
      );
      const validatedSlPrice = validateOptionalFinalLimitPrice(
        symbol,
        values.sl.limitPrice,
      );

      // TODO update orders or something chain promises
      const validatedOrder = {
        ...order,
        size,
        limitPrice,
      };

      // Only include TP/SL if the checkbox is checked
      if (tpslChecked && validatedTpPrice) {
        validatedOrder['tpPrice'] = parseInputToDecimalStr(
          validatedTpPrice,
          marketSpec.priceDecimals,
        );
      }

      if (tpslChecked && validatedSlPrice) {
        validatedOrder['slPrice'] = parseInputToDecimalStr(
          validatedSlPrice,
          marketSpec.priceDecimals,
        );
      }

      queue.push(() =>
        createOrder({
          order: validatedOrder,
          showToast: true,
          waitForConfirm: true,
        }),
      );

      if (
        values.order.orderType != OrderType.LIMIT &&
        tpslChecked &&
        values.tp.limitPrice !== ''
      ) {
        const tpOrder: CreateOrderInput = {
          symbol,
          orderType: OrderType.TAKE_PROFIT,
          isBuy: !order.isBuy,
          size: order.size, // FIXME: should really use values.tp.size, but this is undefined for some reason
          limitPrice: validateFinalLimitPrice(symbol, values.tp.limitPrice),
          reduceOnly: true,
        };
        queue.push(() => createOrder({ order: tpOrder, showToast: false }));
      }

      if (
        values.order.orderType != OrderType.LIMIT &&
        tpslChecked &&
        values.sl.limitPrice !== ''
      ) {
        const slOrder: CreateOrderInput = {
          symbol,
          orderType: OrderType.STOP_LOSS,
          isBuy: !order.isBuy,
          size: order.size, // FIXME: should really use values.sl.size, but this is undefined for some reason
          limitPrice: validateFinalLimitPrice(symbol, values.sl.limitPrice),
          reduceOnly: true,
        };
        queue.push(() => createOrder({ order: slOrder, showToast: false }));
      }

      // Chain all requests to ensure nonce monotonicity and return array of promise results
      let results: CreateOrderResponse[];
      try {
        results = await queue.reduce(
          (
            acc: Promise<CreateOrderResponse[]>,
            curr: () => Promise<CreateOrderResponse>,
          ) =>
            acc.then((results) =>
              curr().then((result) => [...results, result]),
            ),
          Promise.resolve([]),
        );
      } catch (code) {
        console.warn('Order rejected:', code);
        return;
      }

      if (!results.some((item) => item.status === OrderStatus.REJECTED)) {
        if (
          values.order.orderType !== OrderType.MARKET ||
          !preserveMarketOrderFormOnCreate
        ) {
          resetForm();
        } else {
          restoreLimitPriceOnCancel();
        }
      }
    }
  }

  return (
    <FormProvider {...form}>
      <form
        ref={formRef}
        className="relative flex-1"
        onSubmit={form.handleSubmit(onSubmit)}
      >
        <fieldset
          disabled={isMigrationBlocked}
          className="flex h-full min-w-0 flex-col disabled:opacity-50"
        >
          <SideTabs />
          <OrderTypeTabs
            onChange={(_) => {
              setIsSubmitSuccessful(false);
              setTpslChecked(false);
            }}
          />
          <div className="relative min-h-48 flex-1 md:flex-none">
            <div className="absolute inset-0 h-full w-full md:static md:h-auto">
              <ScrollArea className="h-full md:h-auto md:overflow-visible md:[&>div]:!overflow-visible">
                <div className="flex flex-col px-2.5 py-2">
                  <div className="flex h-10 items-center justify-between px-3">
                    <TooltipWrapper
                      content={t('orderForm.availableFundsTooltip')}
                      className="hidden md:block"
                    >
                      <p className="select-none text-vestgrey-100 md:underline">
                        {t('orderForm.availableFunds')}
                      </p>
                    </TooltipWrapper>
                    <p className="font-mono font-bold text-foreground">
                      <AvailableFunds />
                    </p>
                  </div>
                  {orderType === OrderType.MARKET && (
                    <div className="flex h-10 items-center justify-between px-3">
                      <p className="text-vestgrey-100">
                        {t('orderForm.price')}
                      </p>
                      <p className="font-mono text-foreground">
                        <EstFillPrice />
                      </p>
                    </div>
                  )}
                  {orderType === OrderType.LIMIT && (
                    <PriceInput
                      isSuccess={isSubmitSuccessful}
                      symbol={symbol}
                      onPriceChange={(limitPrice) => {
                        form.setValue('order.limitPrice', limitPrice);

                        const limitPriceNum = Number(limitPrice);

                        useOrdersStore.setState({
                          creationFormEstimateFillPrice: Number.isNaN(
                            limitPriceNum,
                          )
                            ? undefined
                            : limitPriceNum,
                        });
                      }}
                    />
                  )}
                  <SizeInput
                    hideBoxesMobile
                    symbol={symbol}
                    isSuccess={isSubmitSuccessful}
                    defaultSizePct={DEFAULT_SIZE_PCT}
                  />
                  {orderType === OrderType.SCALE && (
                    <div className="mb-2.5 mt-1 flex flex-col gap-1 sm:gap-2.5">
                      <PriceInput
                        isSuccess={isSubmitSuccessful}
                        symbol={symbol}
                        label={t('orderForm.startPrice')}
                        onPriceChange={(startPrice) =>
                          form.setValue('startPrice', startPrice)
                        }
                      />

                      <PriceInput
                        isSuccess={isSubmitSuccessful}
                        symbol={symbol}
                        label={t('orderForm.endPrice')}
                        onPriceChange={(endPrice) =>
                          form.setValue('endPrice', endPrice)
                        }
                      />

                      <Controller
                        control={form.control}
                        name="ordersCount"
                        render={({ field }) => (
                          <FormItem className="h-10">
                            <FormLabel>{t('orderForm.ordersCount')}</FormLabel>
                            <Input
                              {...field}
                              className="max-w-[14ch] rounded-none bg-vestgrey-800 font-mono text-base text-foreground transition-colors hover:bg-border"
                              type="text"
                              value={field.value}
                              onChange={(e) => {
                                const validatedValue = validatedDecimalStr(
                                  e.target.value,
                                  0,
                                  10,
                                );

                                field.onChange(validatedValue);
                              }}
                            />
                          </FormItem>
                        )}
                      />
                      <Controller
                        control={form.control}
                        name="sizeSkew"
                        render={({ field }) => (
                          <FormItem className="h-10">
                            <FormLabel className="flex items-center gap-2">
                              <TooltipWrapper
                                content={t('orderForm.sizeSkewTooltip')}
                                className="hidden md:block"
                              >
                                <p className="select-none text-vestgrey-100 md:underline">
                                  {t('orderForm.sizeSkew')}
                                </p>
                              </TooltipWrapper>
                            </FormLabel>
                            <Input
                              {...field}
                              className="max-w-[14ch] rounded-none bg-vestgrey-800 font-mono text-base text-foreground transition-colors hover:bg-border"
                              type="text"
                              value={field.value}
                              onBlur={(e) =>
                                e.target.value.endsWith('.') &&
                                field.onChange(e.target.value.slice(0, -1))
                              }
                              onChange={(e) => {
                                const validatedValue = validatedDecimalStr(
                                  e.target.value,
                                  2,
                                  10,
                                );

                                field.onChange(validatedValue);
                              }}
                            />
                          </FormItem>
                        )}
                      />
                    </div>
                  )}
                  <Collapsible
                    className={cn(
                      orderType !== OrderType.SCALE && 'md:h-[152px]',
                    )}
                    open={tpslChecked}
                  >
                    <div
                      className={cn(
                        'flex h-10 items-center',
                        (orderType === OrderType.LIMIT ||
                          orderType === OrderType.SCALE) &&
                          'justify-between gap-2',
                      )}
                    >
                      <ReduceOnly
                        onChange={(checked) => checked && setTpslChecked(false)}
                      />
                      {orderType !== OrderType.SCALE && !isReduceOnly && (
                        <FormItem
                          className={cn(
                            'group justify-start',
                            orderType === OrderType.LIMIT && 'w-max',
                          )}
                        >
                          <OrderCheckbox
                            id="tpsl-checked"
                            checked={tpslChecked}
                            onCheckedChange={(checked) => {
                              if (checked) {
                                form.setValue('order.reduceOnly', false);
                              } else {
                                form.setValue('tp.limitPrice', '');
                                form.setValue('sl.limitPrice', '');
                              }
                              setTpslChecked(checked === true);
                            }}
                          />
                          <FormLabel
                            className={cn(
                              'cursor-pointer select-none pl-3 transition-colors',
                              'group-hover:text-foreground',
                            )}
                            htmlFor="tpsl-checked"
                          >
                            {t('orderForm.tpsl')}
                          </FormLabel>
                        </FormItem>
                      )}

                      {(orderType === OrderType.LIMIT ||
                        orderType === OrderType.SCALE) && (
                        <FormField
                          control={form.control}
                          name="order.timeInForce"
                          render={({ field }) => (
                            <FormItem className="max-w-max gap-2">
                              <FormLabel>{t('orderForm.tif')}</FormLabel>
                              <TIFSelect
                                value={field.value as TimeInForce}
                                onChange={(value) => field.onChange(value)}
                              />
                            </FormItem>
                          )}
                        />
                      )}
                    </div>
                    <CollapsibleContent className="collapsible-content space-y-3 overflow-visible p-3">
                      <TpslInput
                        orderType={OrderType.TAKE_PROFIT}
                        shouldReset={isSubmitSuccessful && !isSubmitting}
                      />
                      <TpslInput
                        orderType={OrderType.STOP_LOSS}
                        shouldReset={isSubmitSuccessful && !isSubmitting}
                      />
                    </CollapsibleContent>
                  </Collapsible>
                </div>
                <div className="flex flex-1 flex-col justify-end">
                  <div className="flex flex-col px-2.5 pb-4">
                    {orderType === OrderType.MARKET ? (
                      <Slippage isSubmitSuccessful={isSubmitSuccessful} />
                    ) : (
                      <div
                        className={cn(
                          'h-10',
                          orderType === OrderType.SCALE && '-mt-px',
                        )}
                      />
                    )}
                    <FormItem className="h-10">
                      <TooltipWrapper
                        content={
                          <>
                            {t('orderForm.liquidationPriceTooltip1')}
                            <br />
                            <br />
                            {t('orderForm.liquidationPriceTooltip2')}
                          </>
                        }
                      >
                        <p className="underline">
                          {t('orderForm.liquidationPriceMark')}
                        </p>
                      </TooltipWrapper>
                      <p className="font-mono text-vestgrey-50">
                        <EstLiqPrice scaleOrders={scaleOrders} />
                      </p>
                    </FormItem>
                    <FormItem>
                      <p>{t('orderForm.orderValue')}</p>
                      <p className="font-mono text-vestgrey-50">
                        <OrderValue scaleOrders={scaleOrders} />
                      </p>
                    </FormItem>
                    <FormItem className="h-10">
                      <p>{t('orderForm.fee')}</p>
                      <p className="font-mono text-vestgrey-50">
                        <Fee />
                      </p>
                    </FormItem>
                  </div>
                </div>
              </ScrollArea>
            </div>
          </div>
          <SubmitButton tpslChecked={tpslChecked} scaleOrders={scaleOrders} />
        </fieldset>
        <OrderConfirm
          open={isConfirmOpen}
          onOpenChange={(open) => {
            setIsConfirmOpen(open);

            if (!open && !wasConfirmSubmitted.current) {
              restoreLimitPriceOnCancel();
              resetConfirmFlags();
            }

            wasConfirmSubmitted.current = false;
          }}
          onSubmit={() => {
            wasConfirmSubmitted.current = true;
            isConfirmSubmitted.current = true;

            formRef.current?.dispatchEvent(
              new Event('submit', { cancelable: true, bubbles: true }),
            );
          }}
        />
        <ClosePositionConfirm
          open={isClosePositionConfirmOpen}
          onOpenChange={(open) => {
            setIsClosePositionConfirmOpen(open);

            if (!open && !wasConfirmSubmitted.current) {
              restoreLimitPriceOnCancel();
              resetConfirmFlags();
            }

            wasConfirmSubmitted.current = false;
          }}
          onSubmit={() => {
            wasConfirmSubmitted.current = true;
            isClosePositionConfirmSubmitted.current = true;

            formRef.current?.dispatchEvent(
              new Event('submit', { cancelable: true, bubbles: true }),
            );
          }}
        />
        <ScaleOrderConfirm
          open={isScaleOrderConfirmOpen}
          scaleOrders={scaleOrders}
          onOpenChange={(open) => {
            setIsScaleOrderConfirmOpen(open);

            if (!open && !wasConfirmSubmitted.current) {
              resetConfirmFlags();
            }

            wasConfirmSubmitted.current = false;
          }}
          onSubmit={() => {
            wasConfirmSubmitted.current = true;
            isScaleOrderConfirmSubmitted.current = true;

            formRef.current?.dispatchEvent(
              new Event('submit', { cancelable: true, bubbles: true }),
            );
          }}
        />
        <LimitsBreachConfirm
          open={isLimitsBreachConfirmOpen}
          breachInfo={limitsBreachInfo}
          onOpenChange={(open) => {
            setIsLimitsBreachConfirmOpen(open);

            if (!open && !wasConfirmSubmitted.current) {
              restoreLimitPriceOnCancel();
              resetConfirmFlags();
            }

            wasConfirmSubmitted.current = false;
          }}
          onSubmit={() => {
            wasConfirmSubmitted.current = true;
            isLimitsBreachConfirmSubmitted.current = true;

            formRef.current?.dispatchEvent(
              new Event('submit', { cancelable: true, bubbles: true }),
            );
          }}
        />
      </form>
    </FormProvider>
  );
}
